import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

/**
 * Centralized HTTP error handler.
 *
 * This interceptor sits at the bottom of the interceptor chain and catches
 * every HTTP error response so we get consistent logging and Sentry reporting
 * without having to repeat error-handling code in every service/component.
 *
 * What it does per status class:
 *
 *  401  — Session expired or not logged in. Log at warn level; components
 *          that care (e.g. login page) can still catch the re-thrown error.
 *
 *  403  — Authenticated but not authorized. Log at warn; shows a developer
 *          immediately if a route guard or backend permission is misconfigured.
 *
 *  404  — Resource not found. Log at warn; common and usually not actionable
 *          at the global level, but useful to see in the console during dev.
 *
 *  422  — Validation errors returned by the API. Log structured errors so
 *          the full validation payload is visible in DevTools.
 *
 *  5xx  — Server error. Log at error level AND send to Sentry so the team is
 *          alerted. Includes the requestId so the Sentry event can be correlated
 *          directly to a backend log line.
 *
 *  Other — Catch-all: log at error level and send to Sentry.
 *
 * After logging/reporting the error is ALWAYS re-thrown so the calling
 * service / component can still handle it (e.g. show a field-level validation
 * message for 422, redirect on 401, etc.).
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const requestId = error.headers?.get('x-request-id') ?? 'unknown';
        const context = {
          requestId,
          status: error.status,
          url: error.url,
          method: req.method,
        };

        if (error.status === 401) {
          console.warn('[HTTP 401] Unauthorized — session may have expired', context);

        } else if (error.status === 403) {
          console.warn('[HTTP 403] Forbidden — insufficient permissions', context);

        } else if (error.status === 404) {
          console.warn('[HTTP 404] Not found', context);

        } else if (error.status === 422) {
          // Surface the full validation payload so developers can see exactly
          // which fields failed without opening the Network tab.
          console.warn('[HTTP 422] Validation error', { ...context, errors: error.error?.errors ?? error.error });

        } else if (error.status >= 500) {
          // Server errors are unexpected — report to Sentry with the requestId
          // as a tag so you can jump straight to the matching backend log.
          Sentry.withScope((scope) => {
            scope.setTag('requestId', requestId);
            scope.setContext('http', context);
            Sentry.captureException(error);
          });
          console.error('[HTTP 5xx] Server error', context);

        } else {
          // Network error (status 0) or any other unclassified error
          Sentry.withScope((scope) => {
            scope.setTag('requestId', requestId);
            scope.setContext('http', context);
            Sentry.captureException(error);
          });
          console.error('[HTTP error]', context);
        }

        // Always re-throw so downstream handlers (services, components) can
        // react to the specific error (e.g. show a field message for 422).
        return throwError(() => error);
      })
    );
  }
}
